import { createSelector } from 'reselect'
import { AppState } from '@chegg-wt/core/state/types'
import { isStyleFootnote, isStyleGated } from '@chegg-wt/core/utils/styles'
import { hasExpired, isExpiring, expireInDays } from '@chegg-wt/core/utils/projects'
import { POPULAR_STYLES } from '@chegg-wt/core/constants/styles'
import { isGuestSelector, userRoleSelector } from '../user/selectors'

export const currentProjectSelector = (state: AppState) => state.currentProject
export const currentStyleSelector = (state: AppState) => state.currentProject.defaultStyle
export const currentStyleNameSelector = (state: AppState) =>
  state.currentProject.styleName
export const currentUrlStyleNameSelector = (state: AppState) =>
  state.currentProject.urlStyleName
export const projectNameSelector = (state: AppState) => state.currentProject.name
export const currentProjectIdSelector = (state: AppState) => state.currentProject.id
export const currentProjectIsLoadingSelector = (state: AppState) =>
  state.currentProject.isLoading
export const currentProjectCreatedAtSelector = (state: AppState) =>
  state.currentProject.createdAt
export const currentProjectSetInProgressSelector = (state: AppState) =>
  state.currentProject.setInProgress
export const footnoteStyleSelector = createSelector(currentStyleSelector, (styleId) =>
  isStyleFootnote(styleId)
)
export const styleGateFlagSelector = (state: AppState) =>
  state.currentProject.styleGateFlag

export const citationNameSelector = createSelector(
  [currentStyleSelector],
  (currentStyle) => {
    const style = POPULAR_STYLES.find((style) => style.id === currentStyle)
    return style?.name ?? ''
  }
)

export const shouldCheckForExpirationSelector = createSelector(
  [currentProjectIsLoadingSelector, currentProjectCreatedAtSelector],
  (isLoading, createdAt): boolean => !isLoading && !!createdAt
)

export const hasExpiredSelector = createSelector(
  [shouldCheckForExpirationSelector, currentProjectCreatedAtSelector],
  (shouldCheckForExpiration, createdAt): boolean =>
    shouldCheckForExpiration && hasExpired(createdAt)
)

export const isExpiringSelector = createSelector(
  [shouldCheckForExpirationSelector, currentProjectCreatedAtSelector],
  (shouldCheckForExpiration, createdAt): boolean =>
    shouldCheckForExpiration && isExpiring(createdAt)
)

export const expireInDaysSelector = createSelector(
  [isExpiringSelector, currentProjectCreatedAtSelector],
  (isExpiring, createdAt): number | null => (isExpiring ? expireInDays(createdAt) : null)
)
export const shouldShowStyleGatedModalSelector = createSelector(
  [
    userRoleSelector,
    currentStyleSelector,
    currentProjectIdSelector,
    styleGateFlagSelector,
  ],
  (userRole, currentStyle, currentProjectId, isStyleGateFlagEnabled): boolean => {
    if (!currentProjectId || !isStyleGateFlagEnabled) return false

    return isStyleGated(currentStyle, userRole)
  }
)

export const isCurrentGuestGatedStyleSelector = createSelector(
  [isGuestSelector, shouldShowStyleGatedModalSelector],
  (isGuestUser, isCurrentProjectStyleGated): boolean => {
    return isGuestUser && isCurrentProjectStyleGated
  }
)
